import { format } from "date-fns";
import { getRequest } from "../requests";
import { SurveyDefinition } from "../../types/Survey";
import { SurveyResultModel } from "../../types/SurveyResult";

export interface SurveyReference {
    taskID?: string;
    surveyID?: string;
    projectRecordAuthorityCode?: string;
    participantID?: string;
    surveyName?: string;
    linkIdentifier?: string;
    linkKey?: string;
    editResultID?: string;
    event?: string;
}

export interface SurveyCompletionModel {
    ParticipantID?: string;
    surveyTemplate: SurveyDefinition;
    restorationData: string;
    SurveyTemplateVersion: number;
    ParticipantStateAudit: string;
    SurveyID: string;
    TaskID?: string;
    SurveySessionID: string;
    SurveyName: string;
    SurveyDisplayName: string;
    WarningMessage: string;
    ExistingResult?: SurveyResultModel;
    ReplaceResultID?: string;
}

export interface AnonymousSurveyFollowUpModel {
    LinkIdentifier: string;
    Url: string;
}

export function getSurvey(surveyReference: SurveyReference): Promise<SurveyCompletionModel | AnonymousSurveyFollowUpModel> {
    if (surveyReference.taskID) {
        return getSurveyFromTask(surveyReference.taskID);
    } else if (surveyReference.surveyID && surveyReference.projectRecordAuthorityCode) {
        return getSurveyById(surveyReference.surveyID, surveyReference.projectRecordAuthorityCode);
    } else if (surveyReference.linkIdentifier && surveyReference.surveyName) {
        return getAnonymousSurveyByName(surveyReference.linkIdentifier, surveyReference.surveyName);
    } else if (surveyReference.participantID && surveyReference.surveyName) {
        return getSurveyByName(surveyReference.participantID, surveyReference.surveyName, surveyReference.editResultID, surveyReference.event);
    } else if (surveyReference.linkKey) {
        return getSurveyByLinkKey(surveyReference.linkKey);
    }
    throw new Error("Invalid survey reference");
}

export function getCoordinatorIntakeSurvey(projectId: string, surveyId: string) {
	return getRequest(`api/researchkit/administration/projects/${projectId}/coordinatorintakesurveys/${surveyId}`);
}

export function getCoordinatorSurvey(surveyId: string, participantID: string, editResultID?: string, taskID?: string, event?: string) {
    let url = `api/researchkit/administration/coordinatorenteredsurveys/${surveyId}?participantID=${participantID}&offset=${getCurrentOffset()}`;
    if (editResultID) url += `&editResultID=${editResultID}`;
    if (taskID) url += `&taskID=${taskID}`;
    if (event) url += `&event=${encodeURIComponent(event)}`;
    return getRequest(url);
}

function getSurveyFromTask(taskId: string) {
    return getRequest(`api/researchkit/tasks/${taskId}/survey?offset=${getCurrentOffset()}`);
}

function getSurveyById(surveyId: string, projectRACode: string) {
    return getRequest(`api/researchkit/surveys/${surveyId}?studyRACode=${encodeURIComponent(projectRACode)}&offset=${getCurrentOffset()}`);
}

function getSurveyByName(participantID: string, surveyName: string, editResultID?: string, event?: string) {
    const params = new URLSearchParams();
    params.set('surveyName', surveyName);
    params.set('offset', getCurrentOffset());
    if (editResultID) {
        params.set('editResultID', editResultID);
    }
    if (event) {
        params.set('event', event);
    }
    return getRequest(`api/researchkit/participant/${participantID}/surveys/?${params.toString()}`);
}

function getAnonymousSurveyByName(participantLinkIdentifier: string, surveyName: string) {
    return getRequest(`api/researchkit/anonymoussurveys/link/${participantLinkIdentifier}/survey/?surveyName=${encodeURIComponent(surveyName)}&offset=${getCurrentOffset()}`);
}

function getSurveyByLinkKey(linkKey: string) {
    return getRequest(`api/researchkit/surveylink/${linkKey}/survey?offset=${getCurrentOffset()}`);
}

function getCurrentOffset() {
    return encodeURIComponent(format(new Date(), "xxxxx"));
}